import moment from 'moment';

export {
  formatLocalizedDate,
  formatMemberProfileChartDate,
  getMemberProfileChartCutoffDateKey,
  toMemberProfileChartDate,
  toMemberProfileChartDateKey,
} from './dateTime';

export const formatDate = (date) => {
  if (!date) return 'N/A';

  if (typeof date === 'string' && date.match(/^[A-Za-z]+ \d{1,2}(?:st|nd|rd|th) \d{4}$/)) {
    return date;
  }

  const parsedDate = new Date(date);
  if (isNaN(parsedDate.getTime())) return 'N/A';

  const month = parsedDate.toLocaleString('en-US', { month: 'long' });
  const day = parsedDate.getDate();
  const year = parsedDate.getFullYear();
  const suffix = day % 10 === 1 && day !== 11 ? 'st' : day % 10 === 2 && day !== 12 ? 'nd' : day % 10 === 3 && day !== 13 ? 'rd' : 'th';

  return `${month} ${day}${suffix} ${year}`;
};

export const formatCompactNumber = (number, decimalPlaces = 2) => {
  if (!number) return 0;

  const suffixes = [
    { value: 1e12, symbol: 'T' },
    { value: 1e9, symbol: 'B' },
    { value: 1e6, symbol: 'M' },
    { value: 1e3, symbol: 'K' },
    { value: 1, symbol: '' },
  ];

  const formatNumber = (n) => {
    if (n >= 100) return Math.floor(n);
    if (n >= 10) return n.toFixed(Math.min(1, decimalPlaces)).replace(/\.0$/, '');
    return n.toFixed(Math.min(2, decimalPlaces)).replace(/\.00$/, '');
  };

  const absNumber = Math.abs(number);
  const suffix = suffixes.find(s => absNumber >= s.value);

  if (!suffix) return number.toString(); // For numbers smaller than 1

  const scaled = absNumber / suffix.value;
  const formatted = formatNumber(scaled) + suffix.symbol;

  return number < 0 ? '-' + formatted : formatted;
};

export const getRelativeTime = (date) => {
  if (!date) return '';

  const endDate = moment(date);
  const currentDate = moment();

  moment.updateLocale('en', {
    relativeTime: {
      future: function (number) {
        const diffDays = endDate.diff(currentDate, 'days');
        const diffHours = endDate.diff(currentDate, 'hours');
        const diffMins = endDate.diff(currentDate, 'minutes');
        const diffSeconds = endDate.diff(currentDate, 'seconds');

        if (diffDays > 0) {
          const restHours = diffHours - diffDays * 24;
          return `in ${diffDays} ${diffDays < 2 ? 'day' : 'days'}, ${restHours} ${restHours < 2 ? 'hour' : 'hours'
            } `;
        } else if (diffHours > 0) {
          const restMins = diffMins - diffHours * 60;
          return `in ${diffHours} ${diffHours < 2 ? 'hour' : 'hours'}, ${restMins} ${restMins < 2 ? 'minute' : 'minutes'
            } `;
        } else if (diffMins > 0) {
          return `in ${diffMins} ${diffMins < 2 ? 'minute' : 'minutes'}`;
        } else if (diffSeconds > 0) {
          return `in ${diffSeconds} ${diffSeconds < 2 ? 'second' : 'seconds'}`;
        } else {
          return `in ${number}`;
        }
      },
      past: '%s ago',
      s: 'a few seconds',
      ss: '%d seconds',
      m: 'a minute',
      mm: '%d minutes',
      h: 'an hour',
      hh: '%d hours',
      d: 'a day',
      dd: '%d days',
      M: 'a month',
      MM: '%d months',
      y: 'a year',
      yy: '%d years',
    },
  });

  return moment(date).fromNow();
};

export const formatNumber = (number, decimal = 0) => {
  if (!number) return 0;
  return number.toLocaleString(
    undefined, // leave undefined to use the visitor's browser
    { minimumFractionDigits: decimal }
  );
};

export const sortObjectArray = (objs, sortedKey, order = 'asc') => {
  if (!objs) return [];
  const comparison = order === 'desc' ? -1 : 1;
  const newObjs = [...objs];
  newObjs.sort((a, b) =>
    a[sortedKey] > b[sortedKey] ? 1 * comparison : b[sortedKey] > a[sortedKey] ? -1 * comparison : 0
  );
  return newObjs;
};

export const capitalizeFirstLetter = (string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const sortWeekdays = (obj, sortKey) => {
  const data = [...obj];
  const sorter = {
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
    sunday: 7,
  };

  data.sort(function sortByDay(a, b) {
    let day1 = a[sortKey].toLowerCase();
    let day2 = b[sortKey].toLowerCase();
    return sorter[day1] - sorter[day2];
  });

  return data;
};

export const decodeHtmlCharCodes = (str) => {
  return str.replace(/(&#(\d+);)/g, function (match, capture, charCode) {
    return String.fromCharCode(charCode);
  });
};

export const formatPercentageNumber = (n1, n2, decimal = 0) => {
  const num1 = parseFloat(n1);
  const num2 = parseFloat(n2);
  if (!num2) {
    return "0%";
  }
  return (Math.round(num1 / num2 * 100 * Math.pow(10, decimal)) / Math.pow(10, decimal)).toString() + "%";
}
